//On pressing the spacebar it calls the given handler
export function handleSpacebar(event, handler){
  if (event.key === ' ') {
    event.preventDefault();
    handler(event);
  }
}

export function handleEnter(event, handler, blur=true){
  //If enter key was pressed, blur
  if(event.key === 'Enter') {
    event.preventDefault();
    if (blur) event.target.blur()
    handler(event);
  }
}

export function blurOnEnter(event){
  //If enter key was pressed, blur
  if(event.key === 'Enter') {
    event.preventDefault();
    event.target.blur();
  }
}

//Workaround for custom placeholder that grabs the clicks and doesn't forward to native input! E.g. on Chrome Android
export function openNativeInput(event) {
  if (event.target.getInputElement) event.target.getInputElement().then((element) => {
    if (element != null) {
      if (element.showPicker) element.showPicker();
      else if (element.click) element.click();
    }
  });
}

//Workaround for not opening the input on clicking in it the second time! E.g. on Chrome Android
export function forwardClickToIonInput(event) {
  let inputs = event.target.getElementsByTagName('ion-input');
  if (inputs.length > 0 && inputs[0].click) setTimeout(() => inputs[0].click(), 0);
}

export function forwardClickToIonSelect(event) {
  let inputs = event.target.getElementsByTagName('ion-select');
  if (inputs.length > 0 && inputs[0].click) setTimeout(() => inputs[0].click(), 0);
}

//Always blurs and prevents the default handler
//Usually used on elements with hidden elements with tabindex -1
export function cancelFocus(event, shouldScrollIntoView = true) {
  if (shouldScrollIntoView) event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  event.preventDefault();
  event.target.blur();
}

//Creates comparison function that also takes array for comparison (multiple values) into account for the given attribute value of the object
export function createIonSelectComparisonFunction(attributeValue) {
  return (object1, object2) => {
    //Handle array case
    if (Array.isArray(object2)) {
      if (!object1 || !object1[attributeValue]) {
        return false;
      }
      return object2.find(value => value && value[attributeValue] === object1[attributeValue]);
    }
    //Otherwise compare directly
    return object1 && object2 ? object1[attributeValue] === object2[attributeValue] : object1 === object2;
  };
}

/* Calculate the coordinates inside the element based on where on the client window the gloabl coordinates are */
export function getCoordsRelativeToElementFromClientCoords(element, clientX, clientY) {
  let elementBoundary = element.getBoundingClientRect();

  let { width, height } = getComputedStyle(element);
  //Scale on high dpi screens (CSS is smaller than actual value)
  let scalingRatios = { x: 1, y: 1 };
  try {
    if (width != null && element.width != null) {
      let parsedWidth = parseFloat(width.replaceAll(/[^0-9.]/g, ''));
      scalingRatios.x = element.width / parsedWidth;
    }

    if (height != null && element.height != null) {
      let parsedHeight = parseFloat(height.replaceAll(/[^0-9.]/g, ''));
      scalingRatios.y = element.height / parsedHeight;
    }
  } catch {
    //Do not apply if it could not be successfully determined
    scalingRatios = { x: 1, y: 1 };
  }

  let x;
  //Handle edge cases in ifs
  if (clientX < elementBoundary.left) { //Outside left boundary
    x = 0;
  } else if (clientX > elementBoundary.right) { //Outside right boundary
    x = elementBoundary.width;
  } else { //Inside boundary
    x = clientX - elementBoundary.left;
  }

  x *= scalingRatios.x;

  let y;
  //Handle edge cases in ifs
  if (clientY < elementBoundary.top) { //Outside top boundary
    y = 0;
  } else if (clientY > elementBoundary.bottom) { //Outside bottom boundary
    y = elementBoundary.height;
  } else { //Inside boundary
    y = clientY - elementBoundary.top;
  }

  y *= scalingRatios.y;

  return {
    x,
    y
  }
}

export function isCoordsInRectangle(x, y, {xMin, xMax, yMin, yMax}) {
  if (x >= xMin && x <= xMax && y >= yMin && y <= yMax) return true;
  return false;
}

export function holdButton(action, checkIfHeld, debounce = 500, minDebounce = 100, reduceDebounce = 0.9) {
  if (action != null && checkIfHeld != null && checkIfHeld()) {
    action();
    setTimeout(() => holdButton(action, checkIfHeld, Math.max((debounce * reduceDebounce), minDebounce), minDebounce, reduceDebounce), debounce);
  }
}

export function scrollToElementIfNotVisible(element, options) {
  if (element == null) return null;
  // Get the bounding rectangle of the element
  const rect = element.getBoundingClientRect();
  
  // Get the viewport dimensions
  const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
  const windowWidth = (window.innerWidth || document.documentElement.clientWidth);

  // Check if the element is within the viewport
  const isVisible = (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= windowHeight &&
      rect.right <= windowWidth
  );

  // Scroll into view if the element is not visible
  if (!isVisible) {
      element.scrollIntoView(options);
  }
}

//Sets a variable each with the calculated scroll value returned by each function if used on ion-content, and a css class for each classFunction that returns true, and for every child that has the given query selector it runs the function to set scroll position
export function createScrollContentHandler(functions, classFunctions, childPositionSelectors) {
  return function(event) {
    if (event != null && event.target != null) {
      let viewportSize = { width: window.innerWidth, height: window.innerHeight };
      if (functions != null) {
        for (let [targetProperty, functionInstance] of Object.entries(functions)) {
          let calculatedValue = functionInstance((event.detail || event.target), event.target, viewportSize);
          //Only update if it changed to prevent unnecessary redraws
          if (calculatedValue != null && (calculatedValue != event.target.style.getPropertyValue(targetProperty))) {
            event.target.style.setProperty(targetProperty, `${calculatedValue}`);
          }
        }
      }
      if (classFunctions != null) {
        for (let [targetClass, classFunctionInstance] of Object.entries(classFunctions)) {
          //Check in both cases if it is present before updating to prevent unnecessary redraws
          if (classFunctionInstance((event.detail || event.target), event.target, viewportSize)) {
            if (!event.target.classList.contains(targetClass)) event.target.classList.add(targetClass);
          } else {
            if (event.target.classList.contains(targetClass)) event.target.classList.remove(targetClass);
          }
        }
      }
      if (childPositionSelectors != null) {
        for (let [targetChildSelector, childClassFunctionInstance] of Object.entries(childPositionSelectors)) {
          for (let mainElement of event.target.querySelectorAll(targetChildSelector)){
            //Either take the current element, or if it is defined, try to find sub elements in the shadow tree
            let elements = [mainElement];
            if (childClassFunctionInstance.shadowSubPart != null) {
              if (mainElement.shadowRoot) elements = mainElement.shadowRoot.querySelectorAll(childClassFunctionInstance.shadowSubPart);
              else elements = []; //If none found but requested, return no elements
            }
            for (let element of elements) {
              if (childClassFunctionInstance != null && childClassFunctionInstance.valueFunction != null) {
                let calculatedValue = childClassFunctionInstance.valueFunction((event.detail || event.target), element.getBoundingClientRect(), viewportSize);
                let calculatedTargetProperty = childClassFunctionInstance.targetProperty || '--scroll-position';
                //Only update if it changed to prevent unnecessary redraws
                if (calculatedValue != null && (calculatedValue != element.style.getPropertyValue(calculatedTargetProperty))) element.style.setProperty(calculatedTargetProperty, `${calculatedValue}`);
              }
            }
          }
        }
      }
    }
  }
}

//If it is ion-content, try to get the inner scroll element, otherwise it is a normal container, return inner scroll
async function getInnerScrollElement(element) {
  if (element != null) {
    if (element.getScrollElement != null) {
      return element.getScrollElement();
    }
    //The scroll element is a native element that is scrolled itself, just return it
    return element;
  }
  return element; //Undefined return element too
}

export function createCustomScrollbarHandlers() {
  const handleScrollContainerScroll = function(targetContainer) {
    let scrollbarElement = targetContainer.parentElement.querySelector('.custom-scrollbar');
    if (scrollbarElement != null) {
      let targetContainerSize = targetContainer.clientHeight; //scrollHeight includes invisible elements out of bounds. clientHeight is the correct to only get the outer bounds of the visible container.

      getInnerScrollElement(targetContainer).then((scrollContainer) => {
        if (scrollContainer != null) {
          let scrollDifference = (scrollContainer.scrollHeight - targetContainerSize);

          if (scrollDifference > 0) {
            let scrollPercentage = scrollContainer.scrollTop / scrollDifference;
            let scrollbarHeight = 1.0;
            let scrollbarStyles = getComputedStyle(scrollbarElement);
            if (scrollbarStyles != null) {
              scrollbarHeight = parseFloat(scrollbarStyles.height || 1.0) + parseFloat(scrollbarStyles.marginBottom || 0.0) + parseFloat(scrollbarStyles.marginTop || 0.0);
            }
            scrollbarElement.style.visibility = 'visible';
            scrollbarElement.style.top = `${(scrollPercentage * (targetContainerSize - scrollbarHeight))}px`;
          } else {
            scrollbarElement.style.visibility = 'hidden';
          }
        }
      });
    }
  }

  const scrollContent = createScrollContentHandler({
    //Only used to watch the scroll state of the container
    '--scroll': (scrollDetails, targetElement) => {
      handleScrollContainerScroll(targetElement);
      return null; //By returning null, nothing gets changed
    }
  });

  const scrollbarResizeObserver = new ResizeObserver(entries => { //If using, do not forget to disconnect
    if (entries.length > 0) {
      for (let entry of entries) {
        handleScrollContainerScroll(entry.target);
      }
    }
  });

  return { handleScrollContainerScroll, scrollContent, scrollbarResizeObserver };
}

//Returns a number between 0 and 1 for the given range and number
export function scrollProgressScaled(scrollAmount, min, max) {
  if (scrollAmount >= max) return 1.0;
  else if (scrollAmount <= min) return 0.0;

  //Return scale from 0 to 1
  return (scrollAmount - min) / (max - min);
}

export function findNearestScrollParent(elem) {
  let parent = elem.parentNode;
  while (parent) {
    let checkedElement = parent;
    //If it has a shadow DOM and inside is a scroll part, use that to check and potentially return
    if (parent.shadowRoot != null) {
      let shadowScroll = parent.shadowRoot.querySelector("[part='scroll']");
      if (shadowScroll != null) checkedElement = shadowScroll;
    }

    try {
      const style = getComputedStyle(checkedElement);
      if (style) {
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && checkedElement.scrollHeight > checkedElement.clientHeight) {
          return checkedElement;
        }
      }
    } catch {
      //Just continue on an error, getting the computed style
    }
    //Get the outer parent element again, not inside shadow root
    parent = parent.parentNode;
  }
  //Fallback if none found
  return document.scrollingElement || document.documentElement;
}

//Fixes routing issues where transition is not fully completed. Make sure to set the invisibility class!
export function handlePageLeave(pageRef) {
  if (pageRef != null && pageRef.$el != null && pageRef.$el.classList != null) {
    pageRef.$el.classList.add('ion-page-hidden');
  }
}

const VALID_PROTOCOLS = [
  'http://',
  'https://'
]

//Ensures that links start with a valid protocol. If not, assume http and add it in front to prevent relative links being entered and evaluated.
//If a prefix is given, use that. If it is true, use "http://"
//--> https://stackoverflow.com/questions/9215162/html-force-url-hyperlink-to-be-treated-as-non-relative-absolute
export function createLinkPrefix(linkPrefix, linkValue) {
  let usedPrefix = linkPrefix;
  //Always append http when boolean
  if (linkPrefix === true || linkPrefix === false) {
    usedPrefix = 'http://';
  }

  if (linkValue != null) {
    //Boolean -> Check for valid protocols
    if (linkPrefix === true || linkPrefix === false) {
      for (let protocol of VALID_PROTOCOLS) {
        if (linkValue.startsWith(protocol)) {
          return ''; //Already valid, return empty prefix
        }
      }
    } else if (linkPrefix != null && linkPrefix.length > 0) { //A prefix is given, check if it already exists on the link
      if (linkValue.startsWith(linkPrefix)) {
        return '';
      }
    }
  }

  return usedPrefix;
}